import React, { useState, useEffect } from 'react';

import { Button, DropdownMenu, DropdownMenuItem, Tooltip } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import Draggable, { ControlPosition, DraggableData, DraggableEvent } from 'react-draggable';
import classNames from 'classnames';

import styles from './multiple-select-modal.module.scss';

export interface ActionButton {
	icon: BringgFontIcons;
	text: string;
	onClick: () => void;
	className?: string;
	dataTestId?: string;
	tooltip?: JSX.Element | string;
	tooltipOnClick?: JSX.Element | string;
	disabled?: boolean;
	dropdownItems?: DropdownMenuItem[];
	dropdownTrigger?: ('click' | 'hover')[];
}
interface Props {
	selectedCount: number;
	selectedTypeName: string;
	actionButtons: ActionButton[];
	additionalButtons?: DropdownMenuItem[];
	moreText?: string;
	buttonsDisabled?: boolean;
	color?: string;
	onCloseModal: () => void;
	parentSelector?: string;
	positionStorageKey?: string;
}

const MultipleSelectModal = ({
	selectedTypeName,
	actionButtons,
	additionalButtons,
	moreText,
	buttonsDisabled,
	color,
	selectedCount,
	onCloseModal,
	parentSelector,
	positionStorageKey
}: Props) => {
	const [dragModalDisabled, setDragModalDisabled] = useState(true);
	const [position, setPosition] = useState<ControlPosition>({ x: 0, y: 0 });

	useEffect(() => {
		if (positionStorageKey) {
			const savedPosition = localStorage.getItem(positionStorageKey);
			if (savedPosition) {
				try {
					const parsed = JSON.parse(savedPosition) as ControlPosition;
					setPosition(parsed);
				} catch (error) {
					console.warn('Failed to parse saved position from localStorage', error);
				}
			}
		}
	}, [positionStorageKey]);

	const handleDragStop = (_e: DraggableEvent, data: DraggableData) => {
		if (positionStorageKey) {
			const newPosition = { x: data.x, y: data.y };
			setPosition(newPosition);
			localStorage.setItem(positionStorageKey, JSON.stringify(newPosition));
		}
	};

	const onMouseOver = () => {
		if (dragModalDisabled) {
			setDragModalDisabled(false);
		}
	};
	const onMouseOut = () => {
		setDragModalDisabled(true);
	};

	const getButtonContent = (button: ActionButton, disableOnClick = false, showChevron = false) => {
		return (
			<span>
				<Button
					onClick={disableOnClick ? undefined : button.onClick}
					type="text"
					className={classNames(styles.button, button.className, { [styles.buttonWithChevron]: showChevron })}
					disabled={buttonsDisabled || button.disabled}
					data-test-id={button.dataTestId}
				>
					<BringgIcon iconName={button.icon} className={styles.buttonIcon} />
					<div className={styles.buttonContent}>
						<span className={styles.buttonSpan}>{button.text}</span>
						{showChevron && (
							<BringgIcon
								iconName={BringgFontIcons.Chevron}
								className={classNames(styles.buttonIcon, styles.chevronIcon)}
							/>
						)}
					</div>
				</Button>
			</span>
		);
	};

	const getTooltipProps = (button: ActionButton) => {
		if (button.tooltip) {
			return { title: button.tooltip };
		}
		return {
			trigger: ['click'],
			color: 'white',
			className: 'multiselect-modal-action',
			title: button.tooltipOnClick
		};
	};

	return (
		<Draggable
			disabled={dragModalDisabled}
			bounds={parentSelector || 'body'}
			{...(positionStorageKey && { position, onStop: handleDragStop })}
		>
			<div className={styles.modal} data-test-id="multi-select-modal">
				<div className={styles.modalBody}>
					<div className={styles.numberOfTasksBlock} style={{ background: color }}>
						<div
							className={styles.cursorMove}
							onMouseOver={onMouseOver}
							onMouseOut={onMouseOut}
							data-test-id="multiple-select-draggable-action"
						>
							<BringgIcon iconName={BringgFontIcons.Drag} />
						</div>
						<div className={styles.numberOfTasksContent}>
							<span className={styles.numberOfTasks} data-test-id="number-of-selected">
								{selectedCount}
							</span>
							<span className={styles.selectedTypeText}>{selectedTypeName}</span>
						</div>
					</div>
					<div className={classNames(styles.actions, 'multiselect-modal-actions')}>
						{actionButtons.map(button => {
							const hasDropdown = !!button.dropdownItems?.length;

							const mainContent =
								button.tooltip || button.tooltipOnClick ? (
									<Tooltip placement="top" {...getTooltipProps(button)}>
										{getButtonContent(button, hasDropdown, hasDropdown)}
									</Tooltip>
								) : (
									getButtonContent(button, hasDropdown, hasDropdown)
								);

							if (hasDropdown) {
								return (
									<DropdownMenu
										key={button.text}
										items={button.dropdownItems!}
										trigger={button.dropdownTrigger ?? ['click']}
										destroyOnHide
									>
										<div>{mainContent}</div>
									</DropdownMenu>
								);
							}

							return <div key={button.text}>{mainContent}</div>;
						})}
						{!!additionalButtons?.length && (
							<DropdownMenu
								items={additionalButtons}
								noArrow={false}
								trigger={['click']}
								destroyOnHide
								overlayClassName={styles.additionalMenu}
							>
								<Button
									type="text"
									className={styles.button}
									data-test-id="multiple-select-more-button"
									aria-label="multiple-select-more-button"
								>
									<BringgIcon
										iconName={BringgFontIcons.MenuHorizontal}
										className={styles.buttonIcon}
									/>
									<span className={styles.buttonSpan}>{moreText || 'More'}</span>
								</Button>
							</DropdownMenu>
						)}
					</div>
					<Button
						data-test-id="multiple-select-close-button"
						className={styles.close}
						onClick={onCloseModal}
						icon={<BringgIcon iconName={BringgFontIcons.Close} />}
						type="ghost"
						size="large"
					/>
				</div>
			</div>
		</Draggable>
	);
};

export default MultipleSelectModal;
